import { GoalProgressDateRangePreset } from '@griply/types';
import { getGoalProgressDateRange } from '@griply/utils';
import { useGoalById } from 'features/goals';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { PageSectionBase } from 'shared/components/ui/page-section-base';
import { useToday } from 'shared/contexts/today';
import { useUpdateGoalMutation } from 'shared/hooks/queries/use-update-goal-mutation';
import { useUser } from 'shared/hooks/use-user';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

import { DateRangeSelect } from './components/date-range-select';
import { ProgressOverview } from './components/progress-overview';
import * as Styled from './goal-detail.style';
import { useTimeframeTimes } from './hooks/use-timeframe-times';

export const Progress: React.FC = () => {
  const { t } = useTranslation();
  const today = useToday();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);

  const { id } = useParams();
  const goal = useGoalById(id);
  const { mutate: updateGoal } = useUpdateGoalMutation();

  const goalDateRange = useTimeframeTimes(goal);
  const preset =
    goal?.progressDateRangePreset ?? GoalProgressDateRangePreset.Goal;

  const dateRange = useMemo(
    () =>
      getGoalProgressDateRange(preset, {
        goalRange: goalDateRange,
        today,
        weekStartsOn,
      }),
    [preset, goalDateRange, today, weekStartsOn],
  );

  const handleChangePreset = (next: GoalProgressDateRangePreset) => {
    if (!id) {
      return;
    }
    updateGoal({ goal: { id, progressDateRangePreset: next } });
  };

  return (
    <PageSectionBase
      title={t('pages.goal-detail.progress.title')}
      interactions={
        <DateRangeSelect
          customRange={goalDateRange}
          onChange={handleChangePreset}
          value={preset}
        />
      }
      fullSize
      noPadding
    >
      <Styled.ScrollContainer>
        <ProgressOverview dateRange={dateRange} />
      </Styled.ScrollContainer>
    </PageSectionBase>
  );
};
